import "../css/global.css";
import "../css/home.css";
import "../css/responsive.css";

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../../context/Auth/AuthContext";
import { cn } from "../../../handlers/cn.utils";
import useIsLoggedIn from "../../../hooks/use-IsLoggedIn";

// Helper component for navigation items
function NavigationItem({ to, isActive, showMobileMenu, children, onClick }) {
	return (
		<li
			className={cn({
				"w-full": showMobileMenu,
			})}
		>
			<Link
				to={to}
				className={cn("btn text-gray-800 font-medium hover:bg-primary-100 hover:text-primary-700 w-full", {
					"bg-primary-100 text-primary-700": isActive,
				})}
				onClick={onClick}
			>
				{children}
			</Link>
		</li>
	);
}

function Header({ activeLink }) {
	const userLoggedIn = useIsLoggedIn();

	const [activeAboutUsLink, setActiveAboutUsLink] = useState(false);
	const [activeResumeTipsLink, setActiveResumeTipsLink] = useState(false);

	const { logout } = useAuth();

	// Navigation items configuration
	const navigationItems = [
		{
			to: "/jobs",
			label: "Jobs",
			isActive: activeLink === "jobs",
		},
		{
			to: "/companies",
			label: "Companies",
			isActive: activeLink === "companies",
		},
		{
			to: "/resume-tips",
			label: "Resume Tips",
			isActive: activeResumeTipsLink,
		},
		{
			to: "/for-employers",
			label: "For Employers",
			isActive: window.location.pathname.includes("/for-employers"),
		},
		{
			to: "/blog/job-hunt-challenges",
			label: "Blog",
			isActive: window.location.pathname.includes("/blog"),
		},
		{
			to: "/about-us",
			label: "About us",
			isActive: activeAboutUsLink,
		},
		{
			to: "/payments",
			label: "Plans",
			isActive: activeLink === "plans",
		},
	];

	useEffect(() => {
		if (activeLink === "home") {
			setActiveAboutUsLink(false);
			setActiveResumeTipsLink(false);
		}

		if (activeLink === "about") {
			setActiveAboutUsLink(true);
			setActiveResumeTipsLink(false);
		}

		if (activeLink === "tips") {
			setActiveAboutUsLink(false);
			setActiveResumeTipsLink(true);
		}
	}, []);

	const [showMobileMenu, setShowMobileMenu] = useState(false);

	function toggleMenu() {
		setShowMobileMenu(!showMobileMenu);
	}

	const [open, setOpen] = React.useState(false);
	const dropdownRef = React.useRef();

	React.useEffect(() => {
		const handleClick = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClick);

		return () => {
			document.removeEventListener("mousedown", handleClick);
		};
	}, []);

	return (
		<header className={cn("bg-white shadow-sm", { "shadow-none": showMobileMenu })}>
			<div className="container">
				<div className="flex items-center justify-between py-3 md:px-0 px-3">
					<Link to="/" className="logo mr-2">
						<img src="/images/home/logo.svg" className="w-24 h-auto text-gray-900" alt="Dproz logo" />
					</Link>

					<div
						className={cn({
							"flex flex-col z-50 items-start px-2 absolute bg-white w-full top-[60px] shadow-sm pb-6 left-0":
								showMobileMenu,
							"hidden md:flex": !showMobileMenu,
						})}
					>
						<ul
							className={cn("flex items-center justify-evenly 2xl:gap-4", {
								"flex-col justify-start w-full items-start gap-4 z-[500]": showMobileMenu,
							})}
						>
							{navigationItems.map((item) => (
								<NavigationItem key={item.to} to={item.to} isActive={item.isActive} showMobileMenu={showMobileMenu}>
									{item.label}
								</NavigationItem>
							))}
							{showMobileMenu && (
								<>
									{userLoggedIn ? (
										<>
											<NavigationItem to="/my-resumes" isActive={false} showMobileMenu={showMobileMenu}>
												My Resumes
											</NavigationItem>

											<NavigationItem
												to="/signin"
												isActive={false}
												showMobileMenu={showMobileMenu}
												onClick={() => {
													logout();
													window.location.href = "/signin";
												}}
											>
												Logout
											</NavigationItem>
										</>
									) : (
										<>
											<li
												className={cn({
													"w-full": showMobileMenu,
												})}
											>
												<Link
													to="/resume/select-experience"
													className="btn bg-transparent hover:text-primary-600 text-primary-600 font-semibold w-full"
												>
													Create Resume
												</Link>
											</li>
											<NavigationItem to="/signin" isActive={false} showMobileMenu={showMobileMenu}>
												Log In
											</NavigationItem>
											<NavigationItem to="/register" isActive={false} showMobileMenu={showMobileMenu}>
												Register
											</NavigationItem>
										</>
									)}
								</>
							)}
						</ul>
					</div>

					<button className="btn bg-primary-500 ml-auto block md:hidden menu-open text-white" onClick={toggleMenu}>
						{!showMobileMenu ? <span className="fa fa-bars" /> : <span className="fa fa-times" />}
					</button>

					<div className="hidden md:flex items-center">
						<Link to="/resume/select-experience" className="btn text-white hover:text-white bg-primary-600">
							Create Resume
						</Link>
						<div className="lg:ml-3 relative">
							<button
								className="btn border-0 text-primary-600 font-medium hover:text-primary-500"
								type="button"
								onClick={() => setOpen(!open)}
							>
								{userLoggedIn ? "My Resumes" : "Login"}
							</button>
							{open && (
								<div className="absolute bg-white !shadow p-2 !rounded-sm z-50" ref={dropdownRef}>
									{userLoggedIn ? (
										<>
											<Link
												className="flex cursor-pointer items-center !rounded p-2 hover:bg-gray-100/80 min-w-[120px] text-gray-700 hover:text-gray-700"
												to="/my-resumes"
											>
												My Resumes
											</Link>
											<Link
												className="flex cursor-pointer items-center !rounded p-2 hover:bg-gray-100/80 min-w-[120px] text-gray-700 hover:text-gray-700"
												to="/signin"
												onClick={() => {
													logout();
													window.location.href = "/signin";
												}}
											>
												Logout
											</Link>
										</>
									) : (
										<>
											<Link
												className="flex cursor-pointer items-center !rounded p-2 hover:bg-gray-100/80 min-w-[120px] text-gray-700 hover:text-gray-700"
												to="/signin"
											>
												Log In
											</Link>
											<Link
												className="flex cursor-pointer items-center !rounded p-2 hover:bg-gray-100/80 min-w-[120px] text-gray-700 hover:text-gray-700"
												to="/register"
											>
												Register
											</Link>
										</>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</header>
	);
}

export default Header;
